import React, { useEffect, useState } from 'react';
import { Grid, Typography } from '@mui/material';
import { possiblyNavigateToMetadataSearch } from '../../../../lib';
import {
    LabelAndValueComponent,
    updateUserWalletDefaults,
    UpdateUserWalletFormData,
    UpdateUserWalletSchema,
} from './user-wallet-from-helper';
import { TextField } from '@biq/apex-ui';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    useDeleteUserWallet,
    useUpdateUserWallet,
    useUserWalletDetails,
} from '../../../../hooks';
import { WALLETS_AND_GROUPS_PATHS } from 'constants/wallets-and-groups-paths';
import { useAuth, usePermission } from '../../../../core';
import { styles as confirmationModalStyles } from 'components/confirmation-modal/confirmation-modal-helper';
import { walletsAndGroupsCommonStyles } from 'pages/wallets-and-groups/wallets-and-groups-helper';
import { UpdateWalletsAndGroupsWrapper } from '../update-wallets-and-groups-wrapper/UpdateWalletsAndGroupsWrapper';

export const UpdateUserWallet = (): React.JSX.Element => {
    const { getToken } = useAuth();
    const { hasUserCurrentRole } = usePermission();
    const [searchParams] = useSearchParams();
    const {
        handleSubmit,
        control,
        setValue,
        getValues,
        watch,
        formState: { errors },
    } = useForm<UpdateUserWalletFormData>({
        mode: 'all',
        resolver: yupResolver(UpdateUserWalletSchema),
        defaultValues: updateUserWalletDefaults,
    });
    const { id } = useParams();
    const { data: userWalletDetails, isLoading: userWalletDetailsLoading } =
        useUserWalletDetails({
            id: id,
        });
    const { mutate: updateUserWallet } = useUpdateUserWallet();
    const { mutate: deleteUserWallet } = useDeleteUserWallet();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [readOnly, setReadOnly] = useState<boolean>(false);
    const [isChangesUnsaved, setIsChangesUnsaved] = useState<boolean>(false);
    const [openCancelModal, setOpenCancelModal] = useState<boolean>(false);
    const [isEditEnabled, setIsEditEnabled] = useState<boolean>(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (hasUserCurrentRole('sdzAdmin')) {
            setReadOnly(false);
        } else {
            setReadOnly(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!userWalletDetailsLoading) {
            setValue('fullName', userWalletDetails?.data.fullName);
            setValue('email', userWalletDetails?.data.email);
            if (userWalletDetails?.data?.principal) {
                setValue('principal', userWalletDetails?.data.principal);
                setIsEditEnabled(false);
            } else {
                setValue('principal', '');
                setIsEditEnabled(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userWalletDetailsLoading]);

    const handleUserWalletsNavigation = (): void => {
        possiblyNavigateToMetadataSearch(
            navigate,
            `${WALLETS_AND_GROUPS_PATHS.WALLETS_AND_GROUPS_PATH}?option=User Wallets`,
            searchParams
        );
    };

    const onSubmit = (data: UpdateUserWalletFormData): void => {
        setIsChangesUnsaved(false);
        updateUserWallet(
            {
                token: getToken(),
                fullName: data.fullName,
                email: data.email,
                id: id as string,
            },
            {
                onSuccess: () => {
                    setIsLoading(false);
                    setTimeout(() => {
                        handleUserWalletsNavigation();
                    }, 500);
                },
                onError: () => {
                    setIsLoading(false);
                    setIsChangesUnsaved(true);
                },
            }
        );
    };

    const handleCancelButton = (): void => {
        if (isChangesUnsaved) {
            setOpenCancelModal(true);
        } else {
            handleUserWalletsNavigation();
        }
    };

    const handleCancelModalConfirmation = (): void => {
        setOpenCancelModal(false);
        handleUserWalletsNavigation();
    };

    const handleCancelModal = (): void => {
        setOpenCancelModal(false);
    };

    const handleDeleteButton = (): void => {
        setTimeout(() => {
            setIsChangesUnsaved(false);
            handleUserWalletsNavigation();
        }, 500);
        deleteUserWallet({
            token: getToken(),
            id: id as string,
        });
    };

    const handleBackArrowButtonClick = (): void => {
        handleUserWalletsNavigation();
    };

    const renderField = (
        fieldLabel: string,
        fieldValue: 'fullName' | 'email'
    ): React.JSX.Element => {
        return isEditEnabled ? (
            <Grid sx={walletsAndGroupsCommonStyles.textFieldStyles}>
                <Controller
                    name={fieldValue}
                    control={control}
                    render={({ field }): React.JSX.Element => (
                        <TextField
                            {...field}
                            variant='outlined'
                            label={fieldLabel}
                            required
                            error={!!errors?.[fieldValue]}
                            errorText={
                                errors?.[fieldValue]
                                    ? errors?.[fieldValue]?.message
                                    : ''
                            }
                            onChange={(value): void => {
                                field.onChange(value);
                                setIsChangesUnsaved(true);
                            }}
                        />
                    )}
                />
            </Grid>
        ) : (
            <LabelAndValueComponent
                label={fieldLabel}
                value={getValues(fieldValue)}
            />
        );
    };

    const principalValue =
        watch('principal') === '' ? '--' : watch('principal');

    return (
        <UpdateWalletsAndGroupsWrapper
            title={readOnly ? 'View User Wallet' : 'Edit User Wallet'}
            readOnly={readOnly}
            combinedLoading={userWalletDetailsLoading}
            breadCrumData={'User Wallet'}
            handleCancelButton={handleCancelButton}
            handleCancelModal={handleCancelModal}
            onBackArrowClick={handleBackArrowButtonClick}
            deleteMenuItemText={'Delete Wallet'}
            headingText={readOnly ? 'Wallet Info' : 'General Info'}
            onSaveButtonClick={handleSubmit(onSubmit)}
            handleCancelModalConfirmation={handleCancelModalConfirmation}
            isLoading={isLoading}
            isChangesUnsaved={isChangesUnsaved}
            openCancelModal={openCancelModal}
            unsavedChangesConfirmationText={
                'This user wallet has unsaved changes. Are you sure you want to close without saving? You will lose all changes.'
            }
            confirmationKey={getValues('fullName')}
            confirmationButtonText={'Delete User Wallet'}
            confirmationHeadingText={'Confirm User Wallet Deletion'}
            confirmationInputLabelText={`Type ${getValues('fullName')} to confirm`}
            handleConfirmation={handleDeleteButton}
            confirmationSubHeadingText={
                <Typography>
                    By typing{' '}
                    <Typography
                        component='span'
                        sx={confirmationModalStyles.textBoldStyles}
                    >
                        ‘{getValues('fullName')}’
                    </Typography>{' '}
                    below, you understand that you are permanently deleting this
                    user wallet. This action{' '}
                    <Typography
                        component='span'
                        sx={confirmationModalStyles.textBoldStyles}
                    >
                        CANNOT
                    </Typography>{' '}
                    be undone.
                </Typography>
            }
        >
            <Grid
                sx={{
                    ...walletsAndGroupsCommonStyles.containerStyles,
                    p: readOnly ? '15px 44px 31px 44px' : '28px 37px 85px 33px',
                }}
            >
                <LabelAndValueComponent label={'ID'} value={id as string} />
                <LabelAndValueComponent
                    label={'Principal'}
                    value={principalValue}
                />
                {readOnly ? (
                    <LabelAndValueComponent
                        label={'Email Address'}
                        value={getValues().email}
                    />
                ) : (
                    renderField('Email Address', 'email')
                )}
                {readOnly ? (
                    <LabelAndValueComponent
                        label={'Full Name'}
                        value={getValues('fullName')}
                    />
                ) : (
                    renderField('Full Name', 'fullName')
                )}
            </Grid>
        </UpdateWalletsAndGroupsWrapper>
    );
};
